import { isDefined } from "$routes/registers/_lib/is-defined";
import {
  ContactPointOwnership,
  GuardianRelationship,
  NationalIdOwner,
  NationalIdType,
} from "elephant-web/gql";
import type { MigrationStrategies } from "./_types/migration-strategies";

export const PATIENT_MIGRATION_STRATEGIES: MigrationStrategies = {
  // These 3 migrations may seem moot - it's because we'd rolled out v3 schema to production, but hadn't actually
  // put any data in on v0, 1 or 2, so it's safe to just return the old doc as there won't be any. We couldn't revert
  // the schema to v0 because we'd already rolled out v3 to production and RxDB shouts about an RxError DB6 if
  // you don't do migrations properly even if there's no data.
  1: (oldDoc) => oldDoc,
  2: (oldDoc) => oldDoc,
  3: (oldDoc) => oldDoc,
  4: (oldDoc) => {
    oldDoc.address.line4 = oldDoc.address.line4 ?? "";
    return oldDoc;
  },
  5: (oldDoc) => {
    // A bad form default
    if (!oldDoc.insurance?.type) {
      oldDoc.insurance = null;
    }
    // A bad form default
    if (!oldDoc.nextOfKin?.fullName) {
      oldDoc.nextOfKin = null;
    }
    // Pakistan forms weren't correctly mapping the owner field.
    if (isDefined(oldDoc.nationalId?.number)) {
      const nationalId = oldDoc.nationalId;
      oldDoc.nationalId = {
        owner:
          nationalId.type === NationalIdType.Primary
            ? NationalIdOwner.Self
            : nationalId.owner,
        value: nationalId.number,
      };
    }

    // Generically go over all fields set with the value undefined and replace them with null
    function replaceUndefinedWithNull<T>(obj: T): T {
      for (const key in obj) {
        if (obj[key] === undefined) {
          // @ts-ignore - we're replacing undefined with null
          obj[key] = null;
        } else if (typeof obj[key] === "object" && !Array.isArray(obj[key])) {
          replaceUndefinedWithNull(obj[key]);
        }
      }
      return obj;
    }
    replaceUndefinedWithNull(oldDoc);

    return oldDoc;
  },
  6: (oldDoc) => oldDoc,
  7: (oldDoc) => {
    const productMarketing = oldDoc.consent.productMarketing;
    if (typeof productMarketing === "boolean") {
      oldDoc.consent.productMarketing = productMarketing;
    } else if (typeof productMarketing === "string") {
      oldDoc.consent.productMarketing =
        productMarketing.toLowerCase() === "true";
    } else {
      // conservatively defaulting to false
      oldDoc.consent.productMarketing = false;
    }
    return oldDoc;
  },
  8: (oldDoc) => oldDoc,
  9: (oldDoc) => {
    oldDoc.email = null;
    return oldDoc;
  },
  10: (oldDoc) => {
    // biome-ignore lint/performance/noDelete: we're deleting a field that doesn't exist in version 10
    delete oldDoc.card;
    return oldDoc;
  },
  11: (oldDoc) => oldDoc,
  12: (oldDoc) => {
    oldDoc.guardians = null;
    return oldDoc;
  },
  13: (oldDoc) => oldDoc,
  14: (oldDoc) => {
    // Fix corrupted guardian phone ownership values
    // The bug in mapFormValuesToRxPatient caused ownership to be undefined
    // when it should have been a valid ContactPointOwnership value
    if (oldDoc.guardians && Array.isArray(oldDoc.guardians)) {
      oldDoc.guardians.forEach((guardian: any) => {
        if (guardian.phone && guardian.phone.ownership === undefined) {
          // Default to 'personal' as the most likely intended value
          guardian.phone.ownership = ContactPointOwnership.Personal;
        }
      });
    }
    return oldDoc;
  },
  15: (oldDoc) => {
    // Nullify guardian phone objects where ownership would map to null
    // This handles cases where the form stored a phone number but ownership was "None"
    if (oldDoc.guardians && Array.isArray(oldDoc.guardians)) {
      oldDoc.guardians.forEach((guardian: any) => {
        if (guardian.phone && guardian.phone.ownership === null) {
          guardian.phone = null;
        }
      });
    }
    return oldDoc;
  },
  16: (oldDoc) => {
    // Fix missing guardian relationship field
    // Some guardians were saved without the required relationship field
    if (oldDoc.guardians && Array.isArray(oldDoc.guardians)) {
      oldDoc.guardians.forEach((guardian: any) => {
        if (!guardian.relationship) {
          // Default to 'OTHER' as the safest fallback option
          guardian.relationship = GuardianRelationship.Other;
        }
      });
    }
    return oldDoc;
  },
  17: (oldDoc) => {
    // Fix guardian consent saved as boolean string
    if (oldDoc.guardians && Array.isArray(oldDoc.guardians)) {
      oldDoc.guardians.forEach((guardian: any) => {
        if (guardian?.consents?.healthcareComms?.value === "true") {
          guardian.consents.healthcareComms.value = true;
        }
        if (guardian?.consents?.healthcareComms?.value === "false") {
          guardian.consents.healthcareComms.value = false;
        }
        if (guardian?.consents?.marketingComms?.value === "true") {
          guardian.consents.marketingComms.value = true;
        }
        if (guardian?.consents?.marketingComms?.value === "false") {
          guardian.consents.marketingComms.value = false;
        }
      });
    }
    return oldDoc;
  },
  18: (oldDoc) => {
    // Add healthcareComms consent field to patient consent
    if (!oldDoc.consent) {
      oldDoc.consent = {
        elephantUseOfAnonData: false,
        productMarketing: false,
        territoryUseOfData: false,
        accessToHealthRecords: false,
        healthcareComms: false,
      };
    } else if (oldDoc.consent.healthcareComms === undefined) {
      oldDoc.consent.healthcareComms = false;
    }
    return oldDoc;
  },
  19: (oldDoc) => {
    // Fix guardian consent saved as boolean string
    if (oldDoc.guardians && Array.isArray(oldDoc.guardians)) {
      oldDoc.guardians.forEach((guardian: any) => {
        if (guardian?.consents?.healthcareComms?.value === "true") {
          guardian.consents.healthcareComms.value = true;
        }
        if (guardian?.consents?.healthcareComms?.value === "false") {
          guardian.consents.healthcareComms.value = false;
        }
      });
    }
    return oldDoc;
  },
  20: (oldDoc) => {
    // Fix guardian marketingComms consent saved as boolean string
    if (oldDoc.guardians && Array.isArray(oldDoc.guardians)) {
      oldDoc.guardians.forEach((guardian: any) => {
        if (guardian?.consents?.marketingComms?.value === "true") {
          guardian.consents.marketingComms.value = true;
        }
        if (guardian?.consents?.marketingComms?.value === "false") {
          guardian.consents.marketingComms.value = false;
        }
      });
    }
    return oldDoc;
  },
};
